import React, { useState } from 'react';
import { NavLink, Outlet, Navigate, useNavigate } from 'react-router-dom';
import { LayoutDashboard, Users, Store, ClipboardList, Tag, Layers, ArrowLeft, LogOut, Send, Bike, Settings, Truck, Percent, Globe, Package, MessageCircle, Trophy, Menu, X, Map, CheckCircle2, Flame, Sparkles, Receipt, BadgeCheck, Megaphone, ShoppingBag } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

const NAV = [
  { to: '/admin', icon: LayoutDashboard, label: 'Dashboard', end: true },
  { to: '/admin/users', icon: Users, label: 'Users' },
  { to: '/admin/vendors', icon: Store, label: 'Vendors' },
  { to: '/admin/drivers', icon: Bike, label: 'Drivers' },
  { to: '/admin/courier-applications', icon: BadgeCheck, label: 'Courier applications' },
  { to: '/admin/status-updates', icon: Megaphone, label: 'Status updates' },
  { to: '/admin/heatmap', icon: Flame, label: 'Coverage heatmap' },
  { to: '/admin/configuration', icon: Sparkles, label: 'Configuration' },
  { to: '/admin/orders', icon: ClipboardList, label: 'Orders' },
  { to: '/admin/etransfers', icon: Receipt, label: 'E-Transfers' },
  { to: '/admin/completed-orders', icon: CheckCircle2, label: 'Completed orders' },
  { to: '/admin/operations', icon: Trophy, label: 'Operations' },
  { to: '/admin/marketplace-listings', icon: ShoppingBag, label: 'Marketplace listings' },
  { to: '/admin/zones', icon: Map, label: 'Delivery zones' },
  { to: '/admin/delivery-fees', icon: Truck, label: 'Delivery fees' },
  { to: '/admin/shipping-sizes', icon: Package, label: 'Shipping sizes' },
  { to: '/admin/category-pricing', icon: Truck, label: 'Category pricing' },
  { to: '/admin/taxes', icon: Globe, label: 'Taxes' },
  { to: '/admin/promotions', icon: Percent, label: 'Promotions' },
  { to: '/admin/promos', icon: Tag, label: 'Banners' },
  { to: '/admin/whatsapp', icon: MessageCircle, label: 'WhatsApp' },
  { to: '/admin/categories', icon: Layers, label: 'Categories' },
  { to: '/admin/settings', icon: Settings, label: 'Settings' },
];

const AdminLayout = () => {
  const { user, logout, loading } = useAuth();
  const nav = useNavigate();
  const [mobileNavOpen, setMobileNavOpen] = useState(false);

  if (loading) return <div className="h-screen flex items-center justify-center text-slate-500">Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (!user.is_admin) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 px-4">
        <div className="bg-white p-8 rounded-2xl border border-slate-200 max-w-md text-center">
          <div className="h-12 w-12 rounded-full bg-rose-50 text-rose-600 mx-auto flex items-center justify-center font-bold">!</div>
          <h1 className="mt-4 text-xl font-semibold text-slate-900">Admin access required</h1>
          <p className="text-slate-500 mt-1 text-sm">Your account doesn’t have admin permissions.</p>
          <button onClick={() => nav('/')} className="mt-5 px-4 h-10 bg-slate-900 text-white rounded-lg hover:bg-slate-800">Back to app</button>
        </div>
      </div>
    );
  }

  const initials = (user.name || 'A').split(' ').map((s) => s[0]).slice(0, 2).join('').toUpperCase();

  return (
    <div className="min-h-screen bg-slate-50 md:flex">
      {/* Mobile top bar */}
      <div className="md:hidden sticky top-0 z-30 bg-slate-950 text-white flex items-center gap-2 px-3 py-2 shadow-sm" data-testid="admin-mobile-topbar">
        <button
          onClick={() => setMobileNavOpen(true)}
          className="h-9 w-9 rounded-lg bg-slate-800 hover:bg-slate-700 flex items-center justify-center"
          aria-label="Open menu"
          data-testid="admin-mobile-menu-open"
        >
          <Menu className="h-5 w-5" />
        </button>
        <div className="flex items-center gap-2 flex-1 min-w-0">
          <div className="h-7 w-7 rounded-full bg-emerald-600 flex items-center justify-center">
            <Send className="h-3.5 w-3.5 text-white -rotate-12" strokeWidth={2.6} />
          </div>
          <div className="min-w-0">
            <div className="text-[13px] font-semibold truncate leading-tight">ShipitNow</div>
            <div className="text-[9px] uppercase tracking-widest text-emerald-300 leading-none">Admin</div>
          </div>
        </div>
      </div>

      {/* Mobile scrim */}
      {mobileNavOpen && (
        <div
          className="md:hidden fixed inset-0 z-40 bg-black/50"
          onClick={() => setMobileNavOpen(false)}
          data-testid="admin-mobile-scrim"
        />
      )}

      {/* Sidebar */}
      <aside
        className={`w-64 bg-slate-950 text-slate-200 flex flex-col fixed inset-y-0 left-0 z-50 transform transition-transform duration-200 md:translate-x-0 ${mobileNavOpen ? 'translate-x-0' : '-translate-x-full md:translate-x-0'}`}
        data-testid="admin-sidebar"
      >
        <button
          onClick={() => setMobileNavOpen(false)}
          className="md:hidden absolute top-3 right-3 h-8 w-8 rounded-full bg-slate-800 hover:bg-slate-700 flex items-center justify-center"
          aria-label="Close menu"
          data-testid="admin-mobile-menu-close"
        >
          <X className="h-4 w-4" />
        </button>
        <div className="px-5 py-5 border-b border-slate-800">
          <div className="flex items-center gap-2">
            <div className="h-8 w-8 rounded-full bg-emerald-600 flex items-center justify-center">
              <Send className="h-4 w-4 text-white -rotate-12" strokeWidth={2.4} />
            </div>
            <div>
              <div className="font-semibold text-white text-sm leading-tight">ShipitNow</div>
              <div className="text-[10px] uppercase tracking-widest text-emerald-400">Admin</div>
            </div>
          </div>
        </div>
        <nav className="flex-1 p-3 space-y-1 overflow-y-auto">
          {NAV.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.end}
              onClick={() => setMobileNavOpen(false)}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 h-10 rounded-lg text-sm font-medium transition-colors ${isActive ? 'bg-emerald-600 text-white' : 'text-slate-300 hover:bg-slate-800 hover:text-white'}`
              }
            >
              <n.icon className="h-4 w-4" />
              {n.label}
            </NavLink>
          ))}
        </nav>
        <div className="p-3 border-t border-slate-800">
          <button onClick={() => nav('/')} className="w-full flex items-center gap-3 px-3 h-10 rounded-lg text-sm text-slate-300 hover:bg-slate-800 hover:text-white">
            <ArrowLeft className="h-4 w-4" /> Back to app
          </button>
          <div className="mt-2 flex items-center gap-2 px-3 py-2 rounded-lg bg-slate-900">
            <div className="h-8 w-8 rounded-full bg-emerald-600 text-white flex items-center justify-center text-xs font-semibold">{initials}</div>
            <div className="flex-1 min-w-0">
              <div className="text-sm text-white truncate">{user.name}</div>
              <div className="text-[11px] text-slate-400 truncate">{user.email}</div>
            </div>
            <button onClick={() => { logout(); nav('/login'); }} className="text-slate-400 hover:text-rose-400" aria-label="Sign out">
              <LogOut className="h-4 w-4" />
            </button>
          </div>
        </div>
      </aside>

      {/* Content */}
      <div className="flex-1 md:ml-64">
        <main className="p-4 sm:p-6 md:p-8"><Outlet /></main>
      </div>
    </div>
  );
};

export default AdminLayout;
